<template>
  <div class="dropdown-parent-container">
    <button
      type="button"
      title="Toggle Search Menu"
      class="chart-search-menu-options-button dropdown-menu-toggle-arrow-icon-button"
      :class="{ 'dropdown-menu-toggle-arrow-icon-button-open': isOpen }"
      ref="chartSearchDropdownMenuToggleButtonNode"
      @click="toggleDropdown()"
    >
      {{ selectedChartingTool.title }}
      &nbsp;<i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="chartSearchDropdownMenuToggleButtonNode ? [chartSearchDropdownMenuToggleButtonNode] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="chartSearchDropdownMenuToggleButtonNode"
    >
      <template v-slot:body>
        <ul>
          <template
            v-for="chartingTool in CHARTING_TOOL_OPTIONS_ARRAY"
            :key="chartingTool.title"
          >
            <li>
              <button
                type="button"
                @click="[closeDropdown(), setCurrentChartingTool(chartingTool)]"
              >
                <i :class="chartingTool.iconClass"></i>
                &nbsp;{{ chartingTool.title }}
              </button>
              <button
                v-if="chartingTool.allowAsDefaultChartingToolOption"
                class="icon-button dropdown-menu-option-star-button"
                :class="{ 'dropdown-menu-option-icon-button-filled': defaultChartingToolTitle === chartingTool.title }"
                type="button"
                :title="`Set ${chartingTool.title} as Default Tool`"
                @click="setDefaultChartingTool(chartingTool)"
              ><i class="far fa-fw fa-star yellow-icon"></i></button>
            </li>
            <li
              v-if="chartingTool.dividerBelow"
              class="dropdown-divider"
            ></li>
          </template>
        </ul>
      </template>
    </DropdownMenu>
  </div>
</template>

<script lang="ts" setup>
  import { ref, toRefs, watch, onMounted } from 'vue';
  import { useLocalStorage } from '@/composables/useLocalStorage';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import { ChartingToolOption, DEFAULT_CHARTING_TOOL_OPTION, CHARTING_TOOL_OPTIONS_ARRAY } from '@/enums/charting-tool-options';

  const props = defineProps<{
    selectedChartingTool: ChartingToolOption;
  }>();
  const { selectedChartingTool } = toRefs(props);
  const emit = defineEmits<{
    (e: 'update:selectedChartingTool', selectedChartingTool: ChartingToolOption): void;
  }>();
  const {
    getLocalStorageData,
    setLocalStorageData,
    removeLocalStorageData
  } = useLocalStorage();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const chartSearchDropdownMenuToggleButtonNode = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(chartSearchDropdownMenuToggleButtonNode, { windowResize: true });
  const CHARTSEARCH_PREFERENCE_KEY = 'scc-nav-chartSearch-menu-currentFavorite';
  const defaultChartingToolTitle = ref(DEFAULT_CHARTING_TOOL_OPTION.title);

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  function setupChartSearchPreference() {
    ensureValidChartSearchPreferenceValue();
    applyCurrentChartSearchPreference();
  }

  function ensureValidChartSearchPreferenceValue() {
    const preferredChartingToolValue = getLocalStorageData(CHARTSEARCH_PREFERENCE_KEY, '');
    const preferenceBeginsWithNumber = /^[0-9]/.test(preferredChartingToolValue);
    const preferredChartingTool = CHARTING_TOOL_OPTIONS_ARRAY.find(t => t.value === preferredChartingToolValue);

    if (preferenceBeginsWithNumber || !preferredChartingTool || !preferredChartingTool.allowAsDefaultChartingToolOption) {
      window.sc3WorkbenchLogger.error('Invalid chart search preference value detected: ', preferredChartingToolValue);
      removeLocalStorageData(CHARTSEARCH_PREFERENCE_KEY);
    }
  }

  function applyCurrentChartSearchPreference() {
    const preferredChartingToolValue = getLocalStorageData(CHARTSEARCH_PREFERENCE_KEY, '');
    let preferredChartingTool = CHARTING_TOOL_OPTIONS_ARRAY.find(t => t.value === preferredChartingToolValue);

    if (!preferredChartingTool || !preferredChartingTool.allowAsDefaultChartingToolOption) {
      window.sc3WorkbenchLogger.info('Chart search preference value not found or not allowed, setting to default');
      preferredChartingTool = DEFAULT_CHARTING_TOOL_OPTION;
    }

    setDefaultChartingTool(preferredChartingTool);
    setCurrentChartingTool(preferredChartingTool);
  }

  function setCurrentChartingTool(chartingTool: ChartingToolOption) {
    selectedChartingTool.value = chartingTool;
  }

  function setDefaultChartingTool(chartingTool: ChartingToolOption) {
    if (!chartingTool.allowAsDefaultChartingToolOption) {
      return;
    }

    defaultChartingToolTitle.value = chartingTool.title;

    setLocalStorageData(CHARTSEARCH_PREFERENCE_KEY, chartingTool.value);
  }

  watch(selectedChartingTool, () => {
    emit('update:selectedChartingTool', selectedChartingTool.value);
  });

  watch(isOpen, () => {
    if (!isOpen.value) {
      chartSearchDropdownMenuToggleButtonNode.value?.blur();
    }
  });

  onMounted(setupChartSearchPreference);
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
  }

  ul {
    max-height: v-bind('`${dropdownHeight}px`');
  }

  .chart-search-menu-options-button,
  .chart-search-menu-options-button:active,
  .chart-search-menu-options-button:focus {
    padding-top: 0px;
    padding-bottom: 0px;
    padding-left: 10px;
    background-color: #707f8f;
    border: none;
    border-radius: 4px 0px 0px 4px;
    font-size: 14px;
    font-weight: 700;
    color: white;
    transition: all 0.35s ease;
    height: 40px;
    margin: 0px;
    display: inline-block;
    white-space: nowrap;
    vertical-align: middle;
    touch-action: manipulation;
    user-select: none;
  }

  .chart-search-menu-options-button:hover,
  .chart-search-menu-options-button.dropdown-menu-toggle-arrow-icon-button-open {
    background-color: #a9b2bc;
  }

  @media (max-width: 991px) {
    .chart-search-menu-options-button,
    .chart-search-menu-options-button:active,
    .chart-search-menu-options-button:focus {
      font-size: 12px;
    }
  }
</style>
